import React, { Suspense } from "react";
import { createBrowserRouter, Navigate, Outlet } from "react-router-dom";
import type { RouteObject } from "react-router-dom";
import routes from "./routes";
import ConditionalRoute from "@/components/ConditionalRoute/ConditionalRoute";
import PreLogin from "@/layouts/PreLogin/PreLogin";
import PostLogin from "@/layouts/PostLogin/PostLogin";
import PageNotFound from "@/pages/PageNotFound/PageNotFound";
import Loader from "@/components/Loader/Loader";
import RouteErrorBoundary from "@/components/RouteErrorBoundary/RouteErrorBoundary";



export interface RouteConfig {
  path: string;
  element?: React.ReactNode;
  children?: Record<string, RouteConfig> | RouteConfig[];
  pageName?: string;
  sidebar?: { show: boolean };
  permissionName?: string;
}

const buildRoutes = (
  routesInput: Record<string, RouteConfig> | RouteConfig[]
): RouteObject[] => {
  const routesArray: RouteConfig[] = Array.isArray(routesInput)
    ? routesInput
    : Object.values(routesInput);

  return routesArray.map(({ path, element, children }) => ({
    path,
    ...(element ? { element } : {}),
    ...(children ? { children: buildRoutes(children) } : {}),
  }));
};

const router = createBrowserRouter([
  // Unified Root
  {
    path: "/",
    errorElement: <RouteErrorBoundary />,
    children: [
      // Redirect Root to Login
      {
        index: true,
        element: <Navigate to="/login" replace />,
      },

      // Public Layout Section (/login, /sign-up, etc.)
      {
        element: <ConditionalRoute routeMode="guest-only" component={PreLogin} />, 
        children: buildRoutes({
          LOGIN: routes.publicRoutes.LOGIN,
          ACCEPT_INVITE: routes.publicRoutes.ACCEPT_INVITE,
          CREATE_PASSWORD: routes.publicRoutes.CREATE_PASSWORD,
          SIGN_UP: routes.publicRoutes.SIGN_UP,
          FORGOT_PASSWORD: routes.publicRoutes.FORGOT_PASSWORD,
          FORGOT_PASSWORD_OTP: routes.publicRoutes.FORGOT_PASSWORD_OTP,
          EMAIL_VERIFICATION: routes.publicRoutes.EMAIL_VERIFICATION,
        }),
      },

      // Truly Public Section (No login restriction)
      {
        element: (
          <Suspense fallback={<Loader />}>
            <ConditionalRoute routeMode="public" component={() => <Outlet />} />
          </Suspense>
        ),
        children: buildRoutes({
          ASSESSMENT_INVITE_REDIRECT: routes.publicRoutes.ASSESSMENT_INVITE_REDIRECT,
          TAKE_ASSESSMENT: routes.publicRoutes.TAKE_ASSESSMENT,
          ASSESSMENT_PREVIEW: routes.publicRoutes.ASSESSMENT_PREVIEW,
        }),
      },

      // Private Layout Section (/dashboard, /users, etc.)
      {
        element: <ConditionalRoute routeMode="private" component={PostLogin} />,
        children: [
          ...buildRoutes(routes.privateRoutes),
        ],
      },

    ],
  },

  // Catch-all
  {
    path: "*",
    element: <PageNotFound />,
  },
]);

export default router;
